import React, { useState, useEffect } from "react";
import { Form, Input, Button, Alert, Spin, Divider } from "antd";
import { login, ssoLogin, keycloakLogin, handleKeycloakCallback, getKeycloakConfig } from "../auth";

const NAVY = "#1B2A4A";
const INPUT_BG = "#e8edf2";

const LoginPage = ({ history }) => {
  const [loading, setLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [checkingForceSso, setCheckingForceSso] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const hashParams = new URLSearchParams(
      window.location.hash.split("?")[1] || ""
    );

    // Handle Keycloak callback (TSP-129)
    // After Keycloak auth, browser is redirected to: https://tv.tanksoftpro.com/?code=xxx&state=xxx
    const urlParams = new URLSearchParams(window.location.search);
    const kcCode = urlParams.get("code");
    if (kcCode) {
      // returnUrl travels through KC in the `state` param (primary) with
      // sessionStorage as a fallback. state survives Safari ITP and
      // cross-origin navigation where sessionStorage can be cleared.
      const kcState = urlParams.get("state");
      let stateReturnUrl = null;
      if (kcState) {
        try { stateReturnUrl = decodeURIComponent(kcState); } catch (e) { stateReturnUrl = null; }
      }
      sessionStorage.removeItem("kc_login_pending");
      setSsoLoading(true);
      handleKeycloakCallback(kcCode)
        .then(() => {
          const fallbackReturnUrl = sessionStorage.getItem("kc_return_url");
          sessionStorage.removeItem("kc_return_url");
          const target = stateReturnUrl || fallbackReturnUrl || "/needsaction";
          window.history.replaceState(null, "", window.location.pathname + "#" + target);
          window.location.reload();
        })
        .catch((err) => {
          setSsoLoading(false);
          setError(err.message || "SSO login failed");
          window.history.replaceState(null, "", window.location.pathname + "#/login");
        });
      return;
    }

    // Legacy Etendo SSO params (sid / user) and forced KC SSO are mutually
    // exclusive: KC tenants must always go through KC so the session carries
    // an id_token for clean logout. Check forceSso first; if enabled, ignore
    // sid/user even when present on the URL. Non-KC tenants fall back to
    // the legacy ssoLogin path.
    const sid = hashParams.get("sid");
    const ssoUser = hashParams.get("user");
    const returnUrl = hashParams.get("returnUrl");
    const manualMode = hashParams.has("manual");

    const runLegacySso = () => {
      const ssoParams = sid ? { sessionId: sid } : ssoUser ? { username: ssoUser } : null;
      if (!ssoParams) {
        setCheckingForceSso(false);
        return;
      }
      setSsoLoading(true);
      ssoLogin(ssoParams).then((ok) => {
        if (ok) {
          window.location.hash = returnUrl ? "#" + returnUrl : "#/needsaction";
          window.location.reload();
        } else if (sid && ssoUser) {
          return ssoLogin({ username: ssoUser }).then((ok2) => {
            if (ok2) {
              window.location.hash = returnUrl ? "#" + returnUrl : "#/needsaction";
              window.location.reload();
            } else {
              setSsoLoading(false);
              setError("Session expired or invalid. Please sign in manually.");
            }
          });
        } else {
          setSsoLoading(false);
          setError("Session expired or invalid. Please sign in manually.");
        }
      });
    };

    if (manualMode) {
      // Break-glass: skip KC auto-redirect and legacy SSO, show manual form.
      setCheckingForceSso(false);
      return;
    }

    getKeycloakConfig()
      .then((config) => {
        if (config && config.forceSso) {
          keycloakLogin(returnUrl);
        } else {
          runLegacySso();
        }
      })
      .catch(() => runLegacySso());
  }, []);

  const handleSubmit = async (values) => {
    setLoading(true);
    setError("");
    try {
      await login(values.username, values.password);
      const hashParams = new URLSearchParams(window.location.hash.split("?")[1] || "");
      const returnUrl = hashParams.get("returnUrl");
      window.location.hash = returnUrl ? "#" + returnUrl : "#/needsaction";
      window.location.reload();
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleKeycloakLogin = () => {
    setError("");
    const hashParams = new URLSearchParams(window.location.hash.split("?")[1] || "");
    const returnUrl = hashParams.get("returnUrl");
    keycloakLogin(returnUrl);
  };

  if (ssoLoading || checkingForceSso) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f5f6fa",
        }}
      >
        <Spin size="large" tip="Signing in..." />
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f5f6fa",
      }}
    >
      <div style={{ width: 420, textAlign: "center" }}>
        <img
          src="/favicon.png"
          alt="TankSoft Pro"
          style={{ width: 100, height: 98, marginBottom: 24 }}
        />
        <h1
          style={{
            fontSize: 32,
            fontWeight: 600,
            color: NAVY,
            margin: "0 0 40px 0",
            fontFamily: "'Georgia', serif",
          }}
        >
          TankView
        </h1>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ marginBottom: 20, textAlign: "left" }}
          />
        )}

        <Button
          type="primary"
          block
          onClick={handleKeycloakLogin}
          style={{
            height: 50,
            fontSize: 16,
            fontWeight: 600,
            background: "#4B7BEC",
            borderColor: "#4B7BEC",
            borderRadius: 8,
            marginBottom: 8,
          }}
        >
          Sign in with SSO
        </Button>

        <Divider style={{ margin: "16px 0", color: "#999" }}>or</Divider>

        <Form onFinish={handleSubmit} size="large">
          <Form.Item
            name="username"
            rules={[{ required: true, message: "Please enter your username" }]}
          >
            <Input
              placeholder="Username"
              style={{
                height: 50,
                background: INPUT_BG,
                border: "none",
                borderRadius: 8,
                textAlign: "center",
                fontSize: 16,
              }}
            />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[{ required: true, message: "Please enter your password" }]}
          >
            <Input.Password
              placeholder="Password"
              style={{
                height: 50,
                background: INPUT_BG,
                border: "none",
                borderRadius: 8,
                textAlign: "center",
                fontSize: 16,
              }}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              block
              style={{
                height: 50,
                fontSize: 16,
                fontWeight: 600,
                background: NAVY,
                borderColor: NAVY,
                borderRadius: 8,
              }}
            >
              Sign In
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default LoginPage;
